import type { StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { createStore } from './create-store';
import type { TaskKind } from '~/hooks/queries/use-inspector';

// Finished inspector cards the user hasn't dismissed yet. Persisted to local
// storage so the green/red receipt cards survive a browser refresh and stack —
// every undismissed run stays visible until the user clicks its ✕. This is the
// mirror image of `dismissed-skill-runs-store`: that one remembers what was
// closed, this one remembers what to keep showing.

const STORAGE_KEY = 'eddy.pinned-skill-runs';
// High backstop so local storage can't grow without bound after months of use.
// Normal use never reaches it (the user dismisses cards); if it ever did, the
// oldest pinned card drops first (FIFO).
const MAX_CARDS = 100;

/** Everything needed to redraw a finished card after a refresh. The live run
 *  status (turns, sources) and research overlay are re-fetched by the card
 *  itself from durable sources; `isError` is stored so the colour is right even
 *  once the live status has aged out. */
export interface PinnedSkillRun {
  runId: string;
  kind: TaskKind;
  name: string;
  sessionId?: string;
  skillSlug?: string;
  startedAt: number;
  finishedAt: number;
  isError: boolean;
  /** True when the user STOPPED the run (FRONT-186) — drawn as a neutral
   *  "Stopped" card, not a green success one, even after a refresh. */
  cancelled?: boolean;
}

function loadCards(): PinnedSkillRun[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PinnedSkillRun[]) : [];
  } catch {
    return [];
  }
}

function persistCards(cards: PinnedSkillRun[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cards));
  } catch {
    /* quota or storage disabled — pinning becomes session-only */
  }
}

export interface PinnedSkillRunsStore {
  cards: PinnedSkillRun[];
  /** Remember a finished card. No-op if its runId is already pinned. */
  pin: (card: PinnedSkillRun) => void;
  /** Forget a card (on ✕ dismiss). */
  unpin: (runId: string) => void;
  /** Drop every card (account switch — see account-scope.ts, FRONT-732). */
  clear: () => void;
}

export function createPinnedSkillRunsStore(): StoreApi<PinnedSkillRunsStore> {
  return createStore<PinnedSkillRunsStore>('pinned-skill-runs-store', (set, get) => ({
    cards: loadCards(),
    pin: (card) => {
      if (get().cards.some((c) => c.runId === card.runId)) return;
      const next = [...get().cards, card];
      // FIFO trim — drop the oldest if we somehow exceed the backstop.
      const trimmed = next.length > MAX_CARDS ? next.slice(next.length - MAX_CARDS) : next;
      persistCards(trimmed);
      set({ cards: trimmed });
    },
    unpin: (runId) => {
      const cards = get().cards;
      if (!cards.some((c) => c.runId === runId)) return;
      const next = cards.filter((c) => c.runId !== runId);
      persistCards(next);
      set({ cards: next });
    },
    clear: () => {
      persistCards([]);
      set({ cards: [] });
    },
  }));
}

let store: StoreApi<PinnedSkillRunsStore> | undefined;

function getStore(): StoreApi<PinnedSkillRunsStore> {
  if (!store) {
    store = createPinnedSkillRunsStore();
    // Cross-tab sync: a pin/unpin in tab A reflects in tab B within ~2 s (the
    // next background-tasks poll re-renders), matching the dismissed store.
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', (e) => {
        if (e.key !== STORAGE_KEY || !store) return;
        try {
          const parsed = e.newValue ? (JSON.parse(e.newValue) as PinnedSkillRun[]) : [];
          store.setState({ cards: parsed });
        } catch {
          /* malformed value — ignore */
        }
      });
    }
  }
  return store;
}

export function usePinnedSkillRuns<T>(selector: (s: PinnedSkillRunsStore) => T): T {
  return useStore(getStore(), selector);
}

/** Imperative clear (no React) — used by account-scope on user switch (FRONT-732). */
export function clearPinnedSkillRuns(): void {
  getStore().getState().clear();
}
